import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { createModule, emptyContent, listModules, type ContentType } from "@/api/content";
import { Badge, Button, Card, Input, Skeleton } from "@/components";
import { useAuthStore } from "@/state/auth.store";
import { useLang } from "@/i18n/LanguageContext";
import { CONTENT_TYPE_OPTIONS, STATUS_TONE, contentTypeLabel } from "./contentMeta";

const STATUS: Record<string, { nl: string; en: string }> = {
  draft: { nl: "concept", en: "draft" },
  published: { nl: "gepubliceerd", en: "published" },
  archived: { nl: "gearchiveerd", en: "archived" },
};

export function ModulesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useLang();
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = role === "org_admin";

  const modules = useQuery({ queryKey: ["modules"], queryFn: ({ signal }) => listModules(signal) });
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [contentType, setContentType] = useState<ContentType>("document");
  const [requiresAck, setRequiresAck] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !modules.data) return modules.data ?? [];
    return modules.data.filter((m) => m.title.toLowerCase().includes(q));
  }, [modules.data, query]);

  const create = useMutation({
    mutationFn: () => createModule({ title, title_i18n: { nl: title }, content_type: contentType, requires_acknowledgement: requiresAck, content: { nl: emptyContent(contentType) } }),
    onSuccess: (detail) => {
      qc.invalidateQueries({ queryKey: ["modules"] });
      navigate(`/app/content/${detail.id}`);
    },
    onError: (e) => setError(e instanceof Error ? e.message : t("Aanmaken mislukt", "Could not create")),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }

  const statusLabel = (s: string) => (STATUS[s] ? t(STATUS[s].nl, STATUS[s].en) : s);

  return (
    <section>
      <div className="wrap">
        <div className="toolbar">
          <div>
            <p className="eyebrow">Content</p>
            <h2 style={{ fontSize: 30 }}>{t("Modules", "Modules")}</h2>
          </div>
          <div className="spacer" />
          {canEdit && (
            <Button variant={showForm ? "outline" : "primary"} onClick={() => setShowForm((v) => !v)}>
              {showForm ? t("Annuleren", "Cancel") : t("Nieuwe module", "New module")}
            </Button>
          )}
        </div>

        {showForm && canEdit && (
          <Card>
            <form onSubmit={onSubmit}>
              {error && <div className="form-error">{error}</div>}
              <div className="field">
                <label htmlFor="title">{t("Titel", "Title")}</label>
                <input id="title" required value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="ctype">{t("Type", "Type")}</label>
                <select id="ctype" value={contentType} onChange={(e) => setContentType(e.target.value as ContentType)}>
                  {CONTENT_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{t(o.nl, o.en)}</option>
                  ))}
                </select>
              </div>
              <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
                <input type="checkbox" checked={requiresAck} onChange={(e) => setRequiresAck(e.target.checked)} />
                {t("Akkoord vereist (medewerker bevestigt 'gelezen en begrepen')", "Requires acknowledgement (employee confirms 'read and understood')")}
              </label>
              <Button type="submit" disabled={create.isPending}>{create.isPending ? t("Bezig…", "Working…") : t("Aanmaken & bewerken", "Create & edit")}</Button>
            </form>
          </Card>
        )}

        <Card>
          {modules.data && modules.data.length > 0 && (
            <Input name="module-search" placeholder={t("Zoek op titel…", "Search by title…")} value={query} onChange={(e) => setQuery(e.target.value)} />
          )}
          {modules.isLoading ? (
            <Skeleton lines={5} />
          ) : filtered.length > 0 ? (
            <ul className="list-rows">
              {filtered.map((m) => (
                <li key={m.id}>
                  <span style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <Link to={`/app/content/${m.id}`} style={{ fontWeight: 700, color: "var(--ink)" }}>{m.title}</Link>
                    <span style={{ color: "var(--grey-text)", fontSize: 13 }}>
                      {contentTypeLabel(m.content_type, t)} · {m.current_version ? `v${m.current_version}` : t("geen publicatie", "not published")}
                    </span>
                  </span>
                  <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {m.requires_acknowledgement && <Badge>{t("akkoord", "sign-off")}</Badge>}
                    <Badge tone={STATUS_TONE[m.status]}>{statusLabel(m.status)}</Badge>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="no-data-message">
              {modules.data && modules.data.length > 0
                ? t("Geen resultaten.", "No matches.")
                : `${t("Nog geen modules.", "No modules yet.")} ${canEdit ? t("Maak er één aan.", "Create one.") : ""}`}
            </p>
          )}
        </Card>
      </div>
    </section>
  );
}
